import React, { useEffect, useState } from "react";
import { ClipboardCheck, Clock } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { money, num, Spinner, PageHeader, Empty, StatusPill } from "@/components/shared";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const FILTERS = ["Submitted for Approval", "Under Review", "More Information Required", "Approved", "Scheduled for Activation", "Active", "Rejected"];

export default function Approvals() {
  const { user } = useAuth();
  const isAdmin = user.role === "admin";
  const [list, setList] = useState(null);
  const [filter, setFilter] = useState("all");
  const [sel, setSel] = useState(null);
  const [note, setNote] = useState("");
  const [reason, setReason] = useState("");
  const [actDate, setActDate] = useState("");

  const load = () => api.get("/change-requests").then((r) => setList(r.data));
  useEffect(() => { load(); }, []);

  const act = async (fn) => {
    try { await fn(); toast.success("Updated"); setSel(null); setNote(""); setReason(""); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const decide = (status) => act(async () => {
    await api.patch(`/change-requests/${sel.id}/status`, { status, note, rejection_reason: reason, activation_date: actDate || null });
  });
  const activate = () => act(async () => { await api.post(`/change-requests/${sel.id}/activate`); });

  if (!list) return <Spinner />;
  const shown = filter === "all" ? list : list.filter((c) => c.status === filter);

  return (
    <div>
      <PageHeader title="Approval Queue" subtitle="Review, approve and activate client menu change-requests" />
      <div className="flex flex-wrap gap-2 mb-5">
        {["all", ...FILTERS].map((f) => (
          <button key={f} onClick={() => setFilter(f)} data-testid={`cr-filter-${f.replace(/\s/g, "-")}`}
            className={`px-3 py-1.5 rounded-full text-xs font-semibold border ${filter === f ? "border-primary text-amber-300 bg-primary/10" : "border-border text-muted-foreground hover:text-white"}`}>
            {f === "all" ? "All" : f} {f !== "all" && `(${list.filter((c) => c.status === f).length})`}
          </button>
        ))}
      </div>

      {shown.length === 0 ? <Empty icon={ClipboardCheck} title="No requests" /> : (
        <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-4">
          {shown.map((c) => (
            <button key={c.id} onClick={() => { setSel(c); setActDate(c.activation_date || ""); }} data-testid={`cr-card-${c.id}`}
              className="card-surface p-4 text-left hover:border-primary/40 transition-colors">
              <div className="flex items-start justify-between gap-2 mb-1">
                <div className="min-w-0">
                  <p className="font-semibold text-white truncate">{c.proposed?.name || "Menu change"}</p>
                  <p className="text-xs text-muted-foreground truncate">{c.brand_name} · <span className="capitalize">{c.type}</span></p>
                </div>
                {c.overdue && <span className="text-[10px] font-bold text-rose-400 border border-rose-500/40 rounded px-1.5 py-0.5">OVERDUE</span>}
              </div>
              <StatusPill status={c.status} />
              <p className="text-[11px] text-muted-foreground font-mono mt-2">by {c.submitted_by} · {new Date(c.submitted_at).toLocaleDateString()}</p>
            </button>
          ))}
        </div>
      )}

      <Dialog open={!!sel} onOpenChange={(o) => !o && setSel(null)}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto bg-card border-border">
          {sel && (
            <>
              <DialogHeader><DialogTitle className="flex items-center gap-2">{sel.proposed?.name} <StatusPill status={sel.status} /></DialogTitle></DialogHeader>
              <p className="text-xs text-muted-foreground">{sel.brand_name} · <span className="capitalize">{sel.type}</span> · submitted by {sel.submitted_by}</p>

              <div className="grid sm:grid-cols-2 gap-4">
                <Compare title="Current" data={sel.current_snapshot} />
                <Compare title="Proposed" data={sel.proposed} highlight />
              </div>

              {sel.rejection_reason && <p className="text-sm text-rose-400">Rejection reason: {sel.rejection_reason}</p>}
              {sel.more_info_note && <p className="text-sm text-orange-400">Info requested: {sel.more_info_note}</p>}

              <div>
                <p className="text-xs uppercase tracking-wider text-muted-foreground font-mono mb-1.5">History</p>
                <div className="space-y-1 max-h-32 overflow-y-auto">
                  {(sel.history || []).map((h, i) => (
                    <p key={i} className="text-xs text-slate-300"><span className="text-amber-400">{h.action}</span> · {h.by} · {new Date(h.at).toLocaleString()} {h.note ? `— ${h.note}` : ""}</p>
                  ))}
                </div>
              </div>

              {!["Active", "Rejected"].includes(sel.status) && (
                <div className="space-y-3 border-t border-border pt-4">
                  <Textarea placeholder="Internal note / message to client" value={note} onChange={(e) => setNote(e.target.value)} rows={2} data-testid="cr-note" />
                  <div className="flex flex-wrap gap-2">
                    <Button variant="outline" size="sm" onClick={() => decide("Under Review")} data-testid="cr-review">Under Review</Button>
                    <Button variant="outline" size="sm" onClick={() => decide("More Information Required")} data-testid="cr-moreinfo">Request Info</Button>
                    {isAdmin && <Button size="sm" onClick={() => decide("Approved")} data-testid="cr-approve" className="bg-emerald-600 hover:bg-emerald-500 text-white">Approve</Button>}
                    {isAdmin && (
                      <div className="flex items-center gap-1">
                        <Input type="date" value={actDate} onChange={(e) => setActDate(e.target.value)} className="w-40 h-9" />
                        <Button variant="outline" size="sm" onClick={() => decide("Scheduled for Activation")} data-testid="cr-schedule">Schedule</Button>
                      </div>
                    )}
                  </div>
                  {isAdmin && (
                    <div className="flex items-center gap-2">
                      <Input placeholder="Rejection reason" value={reason} onChange={(e) => setReason(e.target.value)} className="flex-1 h-9" data-testid="cr-reason" />
                      <Button variant="outline" size="sm" onClick={() => decide("Rejected")} data-testid="cr-reject" className="text-rose-400 border-rose-500/40">Reject</Button>
                    </div>
                  )}
                  {isAdmin && ["Approved", "Scheduled for Activation"].includes(sel.status) && (
                    <Button onClick={activate} data-testid="cr-activate" className="w-full bg-primary text-primary-foreground hover:bg-amber-400 font-semibold gap-1.5">
                      <ClipboardCheck className="h-4 w-4" /> Activate now (apply to menu)
                    </Button>
                  )}
                  {!isAdmin && <p className="text-xs text-muted-foreground">You can review and comment. Only the Super Admin can approve, reject, schedule or activate.</p>}
                </div>
              )}
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Compare({ title, data, highlight }) {
  return (
    <div className={`rounded-lg border p-3 ${highlight ? "border-primary/40 bg-primary/5" : "border-border"}`}>
      <p className="text-xs uppercase tracking-wider text-muted-foreground font-mono mb-2">{title}</p>
      {!data ? <p className="text-sm text-muted-foreground">—</p> : (
        <div className="space-y-1 text-sm">
          <p className="text-white font-medium">{data.name}</p>
          <p className="text-slate-300">{data.category} · {money(data.selling_price)}</p>
          <div className="pt-1">
            {(data.recipe || []).map((l, i) => (
              <p key={i} className="text-xs text-slate-400">{l.ingredient_name || l.name || l.ingredient_id} — {num(l.qty)}{l.unit ? ` ${l.unit}` : ""}</p>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
